import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, RefreshCw, ArrowRight, CheckCircle2, Ban } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

function money(m: number, currency = "AZN", lang = "az") {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format((m || 0) / 100);
}

const NEXT: Record<string, string> = {
    draft: "review",
    review: "approved",
    approved: "paid",
};
const NEEDED: Record<string, string> = {
    review: "payroll.review",
    approved: "payroll.approve",
    paid: "payroll.pay",
};

export default function PayrollRunDetail() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const { hasPerm } = useAuth();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/payroll/runs/${id}`);
            setData(r.data);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [id]);

    const transition = async (to: string) => {
        setBusy(true); setErr(null);
        try { await api.post(`/payroll/runs/${id}/transition`, { to }); await load(); }
        catch (e: any) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const recalc = async () => {
        setBusy(true); setErr(null);
        try { await api.post(`/payroll/runs/${id}/recalculate`); await load(); }
        catch (e: any) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>;
    if (err) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>;
    if (!data) return null;

    const nextStep = NEXT[data.status];
    const canAdvance = nextStep && hasPerm(NEEDED[nextStep]);
    const canRecalc = ["draft", "review"].includes(data.status) && hasPerm("payroll.calculate");

    return (
        <div className="space-y-4" data-testid="payroll-run-detail">
            <div className="flex items-center gap-3">
                <Link to="/hr/payroll" className="inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: "var(--text-muted)" }}><ArrowLeft size={16} />{t("common.back")}</Link>
                <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }} data-testid="payroll-run-period">
                    {data.period_year}-{String(data.period_month).padStart(2, "0")}
                </h1>
                <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--selected-bg)", color: "var(--accent)" }} data-testid="payroll-run-status-badge">{String(t([`payroll.status.${data.status}`, data.status]))}</span>
                {canRecalc && <button data-testid="payroll-run-recalc-btn" onClick={recalc} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm border transition-colors" style={{ borderColor: "var(--border)", color: "var(--text)" }}><RefreshCw size={14} />{t("payroll.recalculate")}</button>}
                {canAdvance && <button data-testid={`payroll-run-advance-${nextStep}`} onClick={() => transition(nextStep)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    {nextStep === "paid" ? <CheckCircle2 size={14} /> : <ArrowRight size={14} />}
                    {String(t([`payroll.advance.${nextStep}`, nextStep]))}
                </button>}
                {data.status === "draft" && hasPerm("payroll.approve") && <button data-testid="payroll-run-void-btn" onClick={() => transition("voided")} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm border transition-colors" style={{ borderColor: "var(--error-fg)", color: "var(--error-fg)" }}><Ban size={14} />{t("payroll.void")}</button>}
            </div>

            <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                            <th className="text-left px-3 py-2">{t("payroll.item.employee")}</th>
                            <th className="text-left px-3 py-2">{t("payroll.item.rule")}</th>
                            <th className="text-right px-3 py-2">{t("payroll.item.sessions")}</th>
                            <th className="text-right px-3 py-2">{t("payroll.item.hours")}</th>
                            <th className="text-right px-3 py-2">{t("payroll.item.total")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.items || []).map((i: any) => (
                            <tr key={i.id} className="border-t align-top" style={{ borderColor: "var(--border)" }} data-testid={`payroll-item-${i.id}`}>
                                <td className="px-3 py-2" style={{ color: "var(--text)" }}>
                                    <div>{i.employee?.user?.full_name || i.employee?.user?.email || "—"}</div>
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{i.employee?.position}</div>
                                </td>
                                <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>
                                    {String(t([`payroll.rule.${i.rule_type}`, i.rule_type]))}
                                    <details className="text-xs mt-1"><summary style={{ cursor: "pointer" }}>{t("payroll.item.breakdown")}</summary>
                                        <pre className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--text-muted)" }}>{JSON.stringify(i.breakdown, null, 2)}</pre>
                                    </details>
                                </td>
                                <td className="px-3 py-2 text-right" style={{ color: "var(--text)" }}>{i.sessions_count}</td>
                                <td className="px-3 py-2 text-right" style={{ color: "var(--text)" }}>{i.hours_count}</td>
                                <td className="px-3 py-2 text-right font-semibold" style={{ color: "var(--text)" }} data-testid={`payroll-item-total-${i.id}`}>{money(i.total_minor, "AZN", i18n.language)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: "var(--input)" }}>
                            <td className="px-3 py-2 font-semibold" colSpan={4} style={{ color: "var(--text)" }}>{t("payroll.run.total")}</td>
                            <td className="px-3 py-2 text-right font-bold" style={{ color: "var(--text)" }} data-testid="payroll-run-total">
                                {money(data.items_total_minor || 0, "AZN", i18n.language)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
